/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import iconsManifest from 'cryptocurrency-icons/manifest.json';
import defIcon from 'cryptocurrency-icons/svg/color/generic.svg';
import type { PortfolioTableRowData } from '../components/portfolio/PortfolioTableRow';
import { checkFileExistence } from './check-file-existence';
import { isString } from 'lodash-es';

export const minPortfolioItemCostToRender = 5;

export const portfolioDataPollInterval = 30 * 1000;

export const getCryptoIconPath = async (
  rawSymbol: string,
  serverIconPath?: string
) => {
  if (isString(serverIconPath) && serverIconPath?.length > 0) {
    const iconOnServer = await checkFileExistence(serverIconPath);

    if (iconOnServer) return iconOnServer;
  }

  const symbol = rawSymbol.toLowerCase();

  const iconData = iconsManifest.find(
    ({ symbol: manifestSymbol }) => manifestSymbol.toLowerCase() === symbol
  );

  if (iconData) {
    const iconName = iconData.symbol.toLowerCase();

    const iconInLib = await checkFileExistence(
      `/assets/cryptocurrency-icons-lib/${iconName}.svg`
    );

    if (iconInLib) return iconInLib;
  }

  const defFolderSymbolUrl = `/assets/img/icons/${symbol}`;

  const imageInDefFolder = (
    await Promise.all(
      ['svg', 'png', 'jpg'].map((ext) =>
        checkFileExistence(`${defFolderSymbolUrl}.${ext}`)
      )
    )
  ).find((value) => !!value);

  if (imageInDefFolder) return imageInDefFolder;

  return defIcon;
};

export const handlePortfolioTableDataItemEmptyIcon = (
  tableData: PortfolioTableRowData[]
): Promise<PortfolioTableRowData[]> =>
  Promise.all(
    tableData.map(async ({ symbol, icon, ...rest }) => ({
      ...rest,
      symbol,
      icon: await getCryptoIconPath(symbol, icon),
    }))
  );

const stakedCurrenciesEndings = ['.s', '.p', '.m'];

export const isStakedCurrency = (symbol: string) =>
  stakedCurrenciesEndings.some((ending) =>
    symbol.toLowerCase().endsWith(ending)
  );

const currenciesNames = {
  EURO: 'eur',
} as const;

export const isEuroCurrency = (symbol: string) =>
  symbol.toLowerCase() === currenciesNames.EURO;
